package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMCardTitleTextStyle
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMLabelTextStyle
import ai.wagemix.web.theme.WMStatSmallTextStyle
import ai.wagemix.web.theme.WMTabularTextStyle
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import org.jetbrains.compose.web.css.px

/** One line of the occupation list on a saved-mix card. */
interface WMMixCardOccupation {
    val weightText: String
    val name: String

    companion object
}

/** What a saved-mix card shows; the view model builds it from the stored mix and its stats. */
interface WMMixCardPresentation {
    val mixId: String
    val title: String
    /** "3 occupations · Updated Oct 18" */
    val meta: String
    val fromJobDescription: Boolean
    /** The top three occupations by weight. */
    val occupations: List<WMMixCardOccupation>
    /** "$51.8K"; `null` while the stats are loading. */
    val medianText: String?
    /** "Median · 2025" */
    val medianCaption: String
    /** The sparkline's data; `null` while loading. */
    val sparkline: WMBandChartModel?

    companion object
}

fun WMMixCardOccupation.Companion.of(weightText: String, name: String): WMMixCardOccupation =
    WMMixCardOccupationData(weightText, name)

fun WMMixCardPresentation.Companion.of(
    mixId: String,
    title: String,
    meta: String,
    fromJobDescription: Boolean,
    occupations: List<WMMixCardOccupation>,
    medianText: String?,
    medianCaption: String,
    sparkline: WMBandChartModel?
): WMMixCardPresentation = WMMixCardPresentationData(
    mixId, title, meta, fromJobDescription, occupations, medianText, medianCaption, sparkline
)

private data class WMMixCardOccupationData(override val weightText: String, override val name: String): WMMixCardOccupation

private data class WMMixCardPresentationData(
    override val mixId: String,
    override val title: String,
    override val meta: String,
    override val fromJobDescription: Boolean,
    override val occupations: List<WMMixCardOccupation>,
    override val medianText: String?,
    override val medianCaption: String,
    override val sparkline: WMBandChartModel?
): WMMixCardPresentation

/**
 * Penpot `Wagemix / Mix Card`: the AI badge for job-description mixes before the title and meta, the
 * top three occupations, a median figure beside its sparkline, and a right-aligned [actions] row
 * ([Open] [Share]). [menu] sits in the top-right corner (a [WMMenuButton] with the card's other
 * actions). The card is a `listitem`; the grid is its `list`.
 */
@Composable
fun WMMixCard(
    mix: WMMixCardPresentation,
    strings: WMMixCardStrings,
    modifier: Modifier = Modifier,
    menu: (@Composable () -> Unit)? = null,
    actions: @Composable RowScope.() -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    WMCard(modifier = Modifier.role("listitem").then(modifier)) {
        // The badge, the title and the menu share the first line; the meta starts under them at the card's edge.
        Column(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value())) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (mix.fromJobDescription) {
                    WMBadge(strings.fromJobDescription, kind = WMBadgeKind.AI, icon = WMIconGlyph.Sparkles)
                }
                SpanText(
                    mix.title,
                    WMCardTitleTextStyle.toModifier()
                        .fillMaxWidth()
                        .minWidth(0.px)
                        .whiteSpace(WhiteSpace.NoWrap)
                        .overflow(Overflow.Hidden)
                        .textOverflow(TextOverflow.Ellipsis)
                )
                menu?.invoke()
            }
            SpanText(mix.meta, WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant))
        }
        Column(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value()).role("list")) {
            mix.occupations.forEach { occupation ->
                Row(
                    modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()).role("listitem"),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        occupation.weightText,
                        WMLabelStrongTextStyle.toModifier().then(WMTabularTextStyle.toModifier()).minWidth(40.px).flexShrink(0)
                    )
                    SpanText(
                        occupation.name,
                        WMBodySmallTextStyle.toModifier().whiteSpace(WhiteSpace.NoWrap).overflow(Overflow.Hidden).textOverflow(TextOverflow.Ellipsis)
                    )
                }
            }
        }
        Row(modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()), verticalAlignment = Alignment.CenterVertically) {
            Column(modifier = Modifier.gap(WMTightGapVar.value()).flexShrink(0)) {
                SpanText(mix.medianText ?: strings.loading, WMStatSmallTextStyle.toModifier())
                SpanText(mix.medianCaption, WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant))
            }
            Column(modifier = Modifier.fillMaxWidth().height(56.px)) {
                mix.sparkline?.let { WMSparkline(it, description = "${strings.sparklineOf} ${mix.title}") }
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            actions()
        }
    }
}

data class WMMixCardStrings(
    val fromJobDescription: String,
    val loading: String,
    val sparklineOf: String
)

/** A caption-and-label pair used for key/value rows (Account, Payment result). */
@Composable
fun WMKeyValue(label: String, value: String, modifier: Modifier = Modifier) {
    val colors = ColorMode.current.toPalette().fs
    Row(modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()).then(modifier), verticalAlignment = Alignment.CenterVertically) {
        SpanText(label, WMLabelTextStyle.toModifier().color(colors.onSurfaceVariant).fillMaxWidth())
        SpanText(value, WMLabelStrongTextStyle.toModifier().flexShrink(0))
    }
}
